import React, { useEffect, useRef, useState } from 'react'

const BOX_SIZE = 280
const OUTPUT_SIZE = 500

interface Props {
  imageSrc: string
  onCancel: () => void
  onConfirm: (blob: Blob) => void
  confirmLabel?: string
}

export default function PhotoCropModal({ imageSrc, onCancel, onConfirm, confirmLabel = 'Salva foto' }: Props) {
  const imgRef = useRef<HTMLImageElement | null>(null)
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null)
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const dragRef = useRef<{ startX: number; startY: number; origin: { x: number; y: number } } | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const img = new Image()
    img.onload = () => setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight })
    img.src = imageSrc
    imgRef.current = img
  }, [imageSrc])

  if (!naturalSize) {
    return (
      <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center">
        <div className="card p-6 text-sm text-slate-500">Caricamento immagine...</div>
      </div>
    )
  }

  const baseScale = Math.max(BOX_SIZE / naturalSize.w, BOX_SIZE / naturalSize.h)
  const displayedW = naturalSize.w * baseScale * zoom
  const displayedH = naturalSize.h * baseScale * zoom
  const maxOffsetX = Math.max(0, (displayedW - BOX_SIZE) / 2)
  const maxOffsetY = Math.max(0, (displayedH - BOX_SIZE) / 2)

  const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

  const applyOffset = (x: number, y: number) => {
    setOffset({ x: clamp(x, -maxOffsetX, maxOffsetX), y: clamp(y, -maxOffsetY, maxOffsetY) })
  }

  const onPointerDown = (e: React.PointerEvent) => {
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    dragRef.current = { startX: e.clientX, startY: e.clientY, origin: offset }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    const dx = e.clientX - dragRef.current.startX
    const dy = e.clientY - dragRef.current.startY
    applyOffset(dragRef.current.origin.x + dx, dragRef.current.origin.y + dy)
  }
  const onPointerUp = () => { dragRef.current = null }

  const onZoomChange = (newZoom: number) => {
    setZoom(newZoom)
    const newDisplayedW = naturalSize.w * baseScale * newZoom
    const newDisplayedH = naturalSize.h * baseScale * newZoom
    const mx = Math.max(0, (newDisplayedW - BOX_SIZE) / 2)
    const my = Math.max(0, (newDisplayedH - BOX_SIZE) / 2)
    setOffset(o => ({ x: clamp(o.x, -mx, mx), y: clamp(o.y, -my, my) }))
  }

  const handleConfirm = () => {
    setSaving(true)
    const scale = baseScale * zoom
    const sSize = BOX_SIZE / scale
    const sx = naturalSize.w / 2 - (offset.x / scale) - sSize / 2
    const sy = naturalSize.h / 2 - (offset.y / scale) - sSize / 2

    const canvas = document.createElement('canvas')
    canvas.width = OUTPUT_SIZE
    canvas.height = OUTPUT_SIZE
    const ctx = canvas.getContext('2d')
    if (!ctx || !imgRef.current) { setSaving(false); return }
    ctx.drawImage(imgRef.current, sx, sy, sSize, sSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE)
    canvas.toBlob(blob => {
      setSaving(false)
      if (blob) onConfirm(blob)
    }, 'image/jpeg', 0.9)
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="card w-full max-w-sm p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700">Ritaglia foto</h3>

        <div
          className="mx-auto rounded-xl overflow-hidden bg-slate-900 relative cursor-move touch-none select-none"
          style={{ width: BOX_SIZE, height: BOX_SIZE }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <img
            src={imageSrc}
            draggable={false}
            className="absolute top-1/2 left-1/2 pointer-events-none"
            style={{
              width: displayedW,
              height: displayedH,
              maxWidth: 'none',
              maxHeight: 'none',
              transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
            }}
            alt="anteprima ritaglio"
          />
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={e => onZoomChange(parseFloat(e.target.value))}
            className="flex-1"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onCancel} className="btn-ghost text-sm" disabled={saving}>Annulla</button>
          <button onClick={handleConfirm} className="btn-primary text-sm" disabled={saving}>
            {saving ? 'Salvataggio...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
